import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  ScrollView,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, ConfirmationModal, NotesSection, Note, FormHeaderAction } from '../components';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography, withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { QuotationDocumentModal } from '../components/documents/QuotationDocumentModal';

interface QuotationDetailScreenProps {
  quotationId: string;
}

/** Mirrors QuotationFormScreen's parseNotes — backend stores `notes` as a
 * single string that may hold a JSON-serialized array of authored entries. */
const parseNotes = (raw: any): Note[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((note: any) => (typeof note === 'string'
      ? { content: note, createdBy: { id: '', email: '' }, createdAt: new Date() }
      : { ...note, createdAt: note.createdAt ? new Date(note.createdAt) : new Date() }));
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((note: any) => ({ ...note, createdAt: note.createdAt ? new Date(note.createdAt) : new Date() }));
      }
    } catch {
      return [{ content: raw, createdBy: { id: '', email: '' }, createdAt: new Date() }];
    }
  }
  return [];
};

export const QuotationDetailScreen: React.FC<QuotationDetailScreenProps> = ({ quotationId }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();

  const [quotation, setQuotation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadQuotation = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getQuotationById(quotationId);
      if (response.statusCode === 1000 && response.data) {
        setQuotation(response.data);
      } else {
        showNotification('error', t('common.error'), response.errors?.[0]?.message || t('quotations.detail.notFound'));
        navigateTo('quotations');
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), error.message || t('quotations.detail.loadError'));
    } finally {
      setLoading(false);
    }
  }, [quotationId]);

  useEffect(() => { loadQuotation(); }, [quotationId]);

  const [showDocuments, setShowDocuments] = useState(false);

  const handleViewPdf = async (regenerate = false) => {
    try {
      setGeneratingPdf(true);
      const response = await apiService.getQuotationPdf(quotationId, regenerate);
      const pdfUrl = response.data?.pdfUrl;
      const templateUsed = response.data?.templateUsed;
      if (response.statusCode === 1000 && pdfUrl) {
        if (Platform.OS === 'web') {
          window.open(pdfUrl, '_blank');
        } else {
          await Linking.openURL(pdfUrl);
        }
        setQuotation((prev: any) => ({ ...prev, pdfUrl }));
        // Diagnostic: confirms exactly which saved template (or the
        // built-in fallback) rendered this PDF — regenerate only re-renders
        // through whichever template is actually resolved server-side
        // (quotation.templateId, else the business's isDefault template for
        // this type), which may not be the one most recently edited.
        if (regenerate && templateUsed) {
          showNotification('info', t('quotations.detail.regeneratePdf'), `${t('quotations.templateEditor.nameLabel').replace(' *', '')}: ${templateUsed.name}${templateUsed.isDefault ? '' : ' (not default)'}`);
        }
      } else {
        showNotification('error', t('common.error'), response.errors?.[0]?.message || t('quotations.detail.pdfError'));
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), error.message || t('quotations.detail.pdfError'));
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleConvert = async () => {
    try {
      setConverting(true);
      const response = await apiService.convertQuotationToOrder(quotationId);
      if (response.statusCode === 1000 && response.data) {
        showNotification('success', t('quotations.detail.convertedTitle'), t('quotations.detail.convertedMessage', { orderNumber: response.data.orderNumber }));
        navigateTo('order-edit', response.data.orderId);
      } else {
        showNotification('error', t('common.error'), response.errors?.[0]?.message || t('quotations.detail.convertError'));
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), error.message || t('quotations.detail.convertError'));
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await apiService.deleteQuotation(quotationId);
      if (response.statusCode === 1000) {
        showNotification('success', t('common.success'), t('quotations.detail.deletedMessage'));
        navigateTo('quotations');
      } else {
        showNotification('error', t('common.error'), response.errors?.[0]?.message || t('quotations.detail.deleteError'));
        setShowDeleteModal(false);
      }
    } catch (error: any) {
      showNotification('error', t('common.error'), error.message || t('quotations.detail.deleteError'));
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const isConverted = quotation?.quotationStatus === 'converted';

  const headerActions: FormHeaderAction[] = quotation ? [
    ...(!isConverted ? [{
      icon: 'swap-horizontal-outline' as const,
      primary: true,
      onPress: handleConvert,
      loading: converting,
      disabled: converting,
      accessibilityLabel: t('quotations.detail.convertToOrder'),
      label: t('quotations.detail.convertToOrder'),
    }] : []),
    {
      // Always visible (never in the overflow menu) — and always forces a
      // fresh render once a PDF already exists. A plain "View PDF" that
      // just returns the cached pdfUrl belongs in the overflow instead:
      // otherwise editing a quotation's template and tapping the obvious
      // visible button silently keeps showing stale, pre-edit content.
      icon: quotation.pdfUrl ? 'refresh-outline' as const : 'document-text-outline' as const,
      onPress: () => handleViewPdf(!!quotation.pdfUrl),
      loading: generatingPdf,
      disabled: generatingPdf,
      accessibilityLabel: quotation.pdfUrl ? t('quotations.detail.regeneratePdf') : t('quotations.detail.generatePdf'),
      label: quotation.pdfUrl ? t('quotations.detail.regeneratePdf') : t('quotations.detail.generatePdf'),
    },
    ...(quotation.pdfUrl ? [{
      icon: 'eye-outline' as const,
      onPress: () => handleViewPdf(false),
      disabled: generatingPdf,
      accessibilityLabel: t('quotations.detail.viewPdf'),
      label: t('quotations.detail.viewPdf'),
    }] : []),
    {
      icon: 'paper-plane-outline' as const,
      onPress: () => setShowDocuments(true),
      accessibilityLabel: t('documents.sendQuotation'),
      label: t('documents.sendQuotation'),
    },
    ...(!isConverted ? [{
      icon: 'create-outline' as const,
      onPress: () => navigateTo('quotation-edit', quotationId),
      accessibilityLabel: t('quotations.detail.edit'),
      label: t('quotations.detail.edit'),
    }] : []),
    {
      icon: 'trash-outline' as const,
      onPress: () => setShowDeleteModal(true),
      accessibilityLabel: t('quotations.detail.delete'),
      label: t('quotations.detail.delete'),
    },
  ] : [];

  const infoRow = (icon: string, value?: string) => value ? (
    <View style={[styles.infoRow, { flexDirection: rtl.rowDirection }]}>
      <Ionicons name={icon as any} size={16} color={colors.textLight} />
      <Text style={styles.infoValue} numberOfLines={1}>{value}</Text>
    </View>
  ) : null;

  return (
    <ScreenLayout
      header={<Header showNotifications showMessages onNotificationPress={() => navigateTo('notifications')} onMessagePress={() => navigateTo('chat')} showNavigation={Platform.OS === 'web'} activeTab={activeTab} onTabPress={handleTabPress} />}
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title={quotation?.quotationNumber || t('quotations.detail.title')}
          subtitle={quotation?.customer?.name}
          onBack={() => navigateTo('quotations')}
          actions={headerActions}
        />
      }
    >
      <View style={styles.container}>
        {loading ? (
          <View style={styles.centerContainer}><ActivityIndicator size="large" color={colors.primary} /></View>
        ) : (
          <ScrollView style={styles.content} contentContainerStyle={styles.contentInner}>
            <View style={[styles.statusRow, { flexDirection: rtl.rowDirection }]}>
              <View style={[styles.statusBadge, isConverted && styles.statusBadgeConverted]}>
                <Text style={[styles.statusText, isConverted && styles.statusTextConverted]}>{quotation.quotationStatus}</Text>
              </View>
              <Text style={styles.dateText}>{new Date(quotation.createdAt).toLocaleDateString()}</Text>
            </View>

            {quotation.convertedOrderId && (
              <TouchableOpacity style={[styles.convertedBanner, { flexDirection: rtl.rowDirection }]} onPress={() => navigateTo('order-edit', quotation.convertedOrderId)} activeOpacity={0.7}>
                <Ionicons name="checkmark-circle" size={18} color="#7C3AED" />
                <Text style={styles.convertedBannerText}>{t('quotations.detail.viewConvertedOrder')}</Text>
                <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={16} color="#7C3AED" />
              </TouchableOpacity>
            )}

            <View style={styles.card}>
              <Text style={styles.cardTitle}>{t('quotations.detail.customer')}</Text>
              {infoRow('person-outline', quotation.customer?.name)}
              {infoRow('call-outline', quotation.customer?.phone)}
              {infoRow('mail-outline', quotation.customer?.email)}
              {infoRow('location-outline', quotation.customer?.address)}
            </View>

            <View style={styles.card}>
              <Text style={styles.cardTitle}>{t('quotations.detail.items')}</Text>
              {(quotation.items || []).map((item: any, idx: number) => (
                <View key={idx} style={[styles.itemRow, { flexDirection: rtl.rowDirection }, idx === (quotation.items || []).length - 1 && styles.itemRowLast]}>
                  <View style={styles.itemInfo}>
                    <Text style={[styles.itemName, { textAlign: rtl.textStart }]} numberOfLines={1}>{item.product?.name}</Text>
                    <Text style={styles.itemMeta}>{item.quantity} × {quotation.currency} {item.unitPrice}</Text>
                  </View>
                  <Text style={styles.itemTotal}>{quotation.currency} {item.totalPrice}</Text>
                </View>
              ))}
            </View>

            <View style={styles.totalsCard}>
              <View style={[styles.totalsRow, { flexDirection: rtl.rowDirection }]}><Text style={styles.totalsLabel}>{t('quotations.detail.subtotal')}</Text><Text style={styles.totalsValue}>{quotation.currency} {quotation.subtotal}</Text></View>
              {!!quotation.tax && <View style={[styles.totalsRow, { flexDirection: rtl.rowDirection }]}><Text style={styles.totalsLabel}>{t('quotations.detail.tax')}</Text><Text style={styles.totalsValue}>{quotation.currency} {quotation.tax}</Text></View>}
              {(quotation.extraCharges || []).filter((c: any) => !/\(\d+(\.\d+)?%\)$/.test(c.name || '')).map((c: any, idx: number) => (
                <View key={idx} style={[styles.totalsRow, { flexDirection: rtl.rowDirection }]}><Text style={styles.totalsLabel}>{c.name}</Text><Text style={styles.totalsValue}>{quotation.currency} {c.amount}</Text></View>
              ))}
              {!!quotation.shipping && (
                <View style={[styles.totalsRow, { flexDirection: rtl.rowDirection }]}>
                  <Text style={styles.totalsLabel}>{quotation.shippingMethod ? `${t('quotations.detail.shipping')} (${quotation.shippingMethod})` : t('quotations.detail.shipping')}</Text>
                  <Text style={styles.totalsValue}>{quotation.currency} {quotation.shipping}</Text>
                </View>
              )}
              {!!quotation.discount && (
                <View style={[styles.totalsRow, { flexDirection: rtl.rowDirection }]}>
                  <Text style={styles.totalsLabel}>{quotation.couponCode ? `${t('quotations.detail.discount')} (${quotation.couponCode})` : t('quotations.detail.discount')}</Text>
                  <Text style={styles.totalsValue}>-{quotation.currency} {quotation.discount}</Text>
                </View>
              )}
              <View style={[styles.totalsRow, styles.totalsRowGrand, { flexDirection: rtl.rowDirection }]}><Text style={styles.totalsLabelGrand}>{t('quotations.detail.total')}</Text><Text style={styles.totalsValueGrand}>{quotation.currency} {quotation.totalPrice}</Text></View>
            </View>

            {quotation.notes && parseNotes(quotation.notes).length > 0 && (
              <View style={styles.card}>
                <NotesSection notes={parseNotes(quotation.notes)} onAddNote={() => {}} editable={false} title={t('quotations.detail.notes')} />
              </View>
            )}
          </ScrollView>
        )}
      </View>

      <ConfirmationModal
        visible={showDeleteModal}
        title={t('quotations.detail.deleteTitle')}
        message={t('quotations.detail.deleteMessage')}
        confirmText={t('common.delete')}
        type="error"
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteModal(false)}
      />
      {showDocuments && <QuotationDocumentModal quotation={quotation} onClose={() => setShowDocuments(false)} />}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.base },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  content: { flex: 1 },
  contentInner: { padding: spacing.lg, paddingBottom: Platform.OS !== 'web' ? 120 : spacing.xl },
  statusRow: { alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.md },
  statusBadge: { backgroundColor: withAlpha(colors.primary, 0.08), paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: 999 },
  statusText: { fontSize: typography.fontSize.xs, fontWeight: '600', color: colors.primary, textTransform: 'capitalize' },
  statusBadgeConverted: { backgroundColor: '#7C3AED15' },
  statusTextConverted: { color: '#7C3AED' },
  dateText: { fontSize: typography.fontSize.xs, color: colors.textLight },
  convertedBanner: {
    alignItems: 'center', gap: spacing.sm, backgroundColor: '#7C3AED12',
    borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: '#7C3AED30',
    padding: spacing.sm, marginBottom: spacing.md,
  },
  convertedBannerText: { flex: 1, fontSize: typography.fontSize.sm, color: '#7C3AED', fontFamily: typography.fontFamily.medium },
  card: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, padding: spacing.md, marginBottom: spacing.md },
  cardTitle: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark, marginBottom: spacing.sm },
  infoRow: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xs / 2 },
  infoValue: { flex: 1, fontSize: typography.fontSize.sm, color: colors.textDark },
  itemRow: { alignItems: 'center', justifyContent: 'space-between', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border },
  itemRowLast: { borderBottomWidth: 0 },
  itemInfo: { flex: 1 },
  itemName: { fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  itemMeta: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: 2 },
  itemTotal: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark },
  totalsCard: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, padding: spacing.md, marginBottom: spacing.md },
  totalsRow: { justifyContent: 'space-between', paddingVertical: spacing.xs },
  totalsLabel: { fontSize: typography.fontSize.sm, color: colors.textLight },
  totalsValue: { fontSize: typography.fontSize.sm, color: colors.textDark },
  totalsRowGrand: { borderTopWidth: 1, borderTopColor: colors.border, marginTop: spacing.xs, paddingTop: spacing.sm },
  totalsLabelGrand: { fontSize: typography.fontSize.base, fontWeight: '700', color: colors.primary },
  totalsValueGrand: { fontSize: typography.fontSize.base, fontWeight: '700', color: colors.primary },
});
